import { useSelection } from "@/contexts/SelectionContext";
import type { SlideDomainEnum } from "@/generated/graphql";
import { SLIDE_DOMAINS } from "@/lib/constants/slides";
import { useAddSlide } from "@/lib/hooks/slides/useAddSlide";
import { useEditSlideTemplate } from "@/lib/hooks/slides/useEditSlideTemplate";
import { useSetSlideDomains } from "@/lib/hooks/slides/useSetSlideDomains";
import { useSetSlideTags } from "@/lib/hooks/slides/useSetSlideTags";
import { useTags } from "@/lib/hooks/tags/useTags";
import { CREATABLE_TEMPLATES, getTemplateLabel, toTemplateEnum } from "@/lib/slides/slideTemplates";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeEditSlideModal } from "@/store/uiSlice";
import type { SlideTemplate } from "@ai-teach/types";
import { resolveSlideTemplate } from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { HiPencil, HiPlus, HiSparkles, HiX } from "react-icons/hi";
import { type EditableTag, TagModal } from "../../modal/TagModal";
import { TemplatePreview } from "./TemplatePreview";

interface AddSlideModalProps {
  lessonId: string;
}

// Selection order is not meaningful, so only membership decides whether to fire the mutation.
function sameMembers<T>(a: readonly T[], b: readonly T[]) {
  return a.length === b.length && a.every((item) => b.includes(item));
}

export function AddSlideModal({ lessonId }: AddSlideModalProps) {
  const { editingSlide, subslideParent } = useAppSelector((state) => state.ui);

  const [slideName, setSlideName] = useState(editingSlide?.name ?? "");
  const [selectedTemplate, setSelectedTemplate] = useState<SlideTemplate | null>(
    resolveSlideTemplate(editingSlide)
  );
  const [selectedDomains, setSelectedReports] = useState<SlideDomainEnum[]>(
    editingSlide?.domains ?? []
  );
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(editingSlide?.tagIds ?? []);
  const [tagModal, setTagModal] = useState<{ tag: EditableTag | null } | null>(null);

  const { addSlide } = useAddSlide();
  const { editSlideTemplate } = useEditSlideTemplate();
  const { setSlideDomains } = useSetSlideDomains();
  const { setSlideTags } = useSetSlideTags();
  const { tags } = useTags();
  const navigate = useNavigate();

  const { toggleWarningModal } = useSelection();

  const dispatch = useAppDispatch();

  const toggleDomain = (domain: SlideDomainEnum) => {
    setSelectedReports((prev) =>
      prev.includes(domain) ? prev.filter((r) => r !== domain) : [...prev, domain]
    );
  };

  const handleCloseModal = () => {
    dispatch(closeEditSlideModal());
    setSlideName("");
    setSelectedTemplate(null);
    setSelectedReports([]);
    setSelectedTagIds([]);
    setTagModal(null);
  };

  // Tags drive lesson section navigation and domains decide where the score counts, so an
  // untagged or undomained slide is unreachable rather than merely unclassified.
  const canSubmit =
    Boolean(slideName.trim()) &&
    selectedTemplate !== null &&
    selectedTagIds.length > 0 &&
    selectedDomains.length > 0;

  const handleCreateSlide = async () => {
    if (!lessonId || !canSubmit || !selectedTemplate) return;

    setIsCreating(true);
    if (editingSlide === null) {
      try {
        const createdSlides = await addSlide({
          lessonId,
          name: slideName.trim(),
          template: toTemplateEnum(selectedTemplate),
          tagIds: selectedTagIds,
          domains: selectedDomains,
          ...(subslideParent && { parentSlideId: subslideParent.id }),
        });

        const originalSlide = createdSlides?.find((slide) => slide.lessonId === lessonId);

        if (originalSlide) {
          saveScrollTarget(`slide-${originalSlide.id}`, lessonId);
          navigate({ to: "/admin/slide/$slideId", params: { slideId: originalSlide.id } });
        }
      } finally {
        handleCloseModal();
        setIsCreating(false);
      }
    } else {
      const performEdit = async () => {
        try {
          if (!editingSlide.id) return;

          await editSlideTemplate(
            editingSlide.id,
            slideName.trim(),
            toTemplateEnum(selectedTemplate)
          );

          if (!sameMembers(editingSlide.domains ?? [], selectedDomains)) {
            await setSlideDomains([editingSlide.id], selectedDomains);
          }

          if (!sameMembers(editingSlide.tagIds ?? [], selectedTagIds)) {
            await setSlideTags([editingSlide.id], selectedTagIds);
          }

          handleCloseModal();
        } finally {
          setIsCreating(false);
        }
      };

      // Changing the template can reset slide data, so warn first. Tag and domain
      // changes are non-destructive.
      if (resolveSlideTemplate(editingSlide) !== selectedTemplate) {
        toggleWarningModal("Changing the template will remove existing data", {
          onConfirm: performEdit,
          onCancel: () => setIsCreating(false),
        });
      } else {
        await performEdit();
      }
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleTagSaved = (tag: EditableTag) => {
    setSelectedTagIds((prev) => (prev.includes(tag.id) ? prev : [...prev, tag.id]));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && slideName.trim() && selectedTemplate) {
      handleCreateSlide();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-200 dark:border-gray-800">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="p-2 rounded-xl bg-primary-500/10 text-primary-500">
            <HiSparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="card-title text-secondary-200">
              {editingSlide
                ? "Edit Slide"
                : subslideParent
                  ? "Create Subslide"
                  : "Create New Slide"}
            </h2>
            <p className="caption text-gray-500">
              {subslideParent
                ? `Plays right after "${subslideParent.name}", always in this order.`
                : "Name it, then pick a template, tags and domains."}
            </p>
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={handleCloseModal}
            className="ml-auto rounded-full"
            aria-label="Close modal"
          >
            <HiX className="w-5 h-5" />
          </IconButton>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
          {/* Slide Name Input */}
          <div>
            <label htmlFor="slideName" className="block label mb-2 text-secondary-200">
              Slide Name <span className="text-error-500">*</span>
            </label>
            <input
              id="slideName"
              type="text"
              value={slideName}
              onChange={(e) => setSlideName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter slide name..."
              className="w-full px-4 py-2.5 bg-background border border-gray-200 dark:border-gray-800 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition-all text-secondary-200 placeholder:text-gray-500"
            />
          </div>

          {/* Template Selection */}
          <div>
            <label className="block label mb-3 text-secondary-200">
              Template <span className="text-error-500">*</span>
            </label>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
              {CREATABLE_TEMPLATES.map((value) => {
                const isSelected = selectedTemplate === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSelectedTemplate(value)}
                    // Resting cards are frameless so the preview's own frame is the only edge;
                    // selection and hover are drawn with a ring that sits on top of it.
                    className={`group relative flex flex-col items-center gap-2 p-2 rounded-2xl ring-1 transition-all ${
                      isSelected
                        ? "ring-2 ring-accent-500 bg-accent-500/10"
                        : "ring-transparent hover:bg-gray-100 dark:hover:bg-white/5"
                    }`}
                  >
                    <TemplatePreview template={value} />
                    <span
                      className={`caption font-medium text-center leading-tight h-8 flex items-center ${
                        isSelected ? "text-accent-500" : "text-secondary-200"
                      }`}
                    >
                      {getTemplateLabel(value)}
                    </span>
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-5 h-5 bg-accent-500 rounded-full flex items-center justify-center shadow">
                        <FaCheck className="text-white" fontSize={10} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tag Selection */}
          <div>
            <label className="block label mb-1 text-secondary-200">
              Tag <span className="text-error-500">*</span>
            </label>
            {/* Pick order is stored order, and anything that needs a single tag takes the first. */}
            <p className="mb-3 text-xs text-secondary-400">
              Numbered by pick order — #1 is the one used where only one tag fits.
            </p>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const pickOrder = selectedTagIds.indexOf(tag.id) + 1;
                const isSelected = pickOrder > 0;
                return (
                  // The edit control is a sibling, not a child: a button cannot nest in a button.
                  <div key={tag.id} className="relative group">
                    <button
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      style={{
                        borderColor: tag.color,
                        backgroundColor: isSelected ? tag.color : undefined,
                      }}
                      className={`h-9 pl-4 pr-8 rounded-full border label whitespace-nowrap transition-colors flex items-center gap-2 ${
                        isSelected
                          ? "text-white shadow-sm"
                          : "text-secondary-200 hover:bg-black/5 dark:hover:bg-white/5"
                      }`}
                    >
                      {isSelected ? (
                        <span className="w-4 h-4 rounded-full bg-white/25 text-[10px] leading-none flex items-center justify-center">
                          {pickOrder}
                        </span>
                      ) : (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      {tag.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTagModal({ tag })}
                      aria-label={`Edit ${tag.name}`}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${
                        isSelected ? "text-white" : "text-gray-500 hover:text-accent-500"
                      }`}
                    >
                      <HiPencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => setTagModal({ tag: null })}
                className="h-9 px-4 rounded-full border border-dashed border-gray-300 dark:border-gray-700 label whitespace-nowrap text-gray-500 hover:border-accent-500 hover:text-accent-500 transition-colors flex items-center gap-1.5"
              >
                <HiPlus className="w-4 h-4" />
                Add new tag
              </button>
            </div>
          </div>

          {/* Domain Selection */}
          <div>
            <label className="block label mb-1 text-secondary-200">
              Domain <span className="text-error-500">*</span>
            </label>
            {/* Pick order is stored order, and anything that needs a single domain takes the first. */}
            <p className="mb-3 text-xs text-secondary-400">
              Numbered by pick order — #1 is the one used where only one domain fits.
            </p>
            <div className="flex flex-wrap gap-2">
              {SLIDE_DOMAINS.map((domain) => {
                const pickOrder = selectedDomains.indexOf(domain.enum) + 1;
                const isSelected = pickOrder > 0;
                return (
                  <button
                    key={domain.enum}
                    type="button"
                    onClick={() => toggleDomain(domain.enum)}
                    className={`h-9 px-4 rounded-full border label whitespace-nowrap transition-colors flex items-center gap-2 ${
                      isSelected
                        ? "bg-accent-500 border-accent-500 text-white shadow-sm"
                        : "border-gray-200 dark:border-gray-800 text-secondary-200 hover:border-accent-500 hover:bg-accent-500/10"
                    }`}
                  >
                    {isSelected && (
                      <span className="w-4 h-4 rounded-full bg-white/25 text-[10px] leading-none flex items-center justify-center">
                        {pickOrder}
                      </span>
                    )}
                    {domain.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <Button variant="ghost" onClick={handleCloseModal} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateSlide} disabled={!canSubmit || isCreating}>
            {isCreating ? "Creating..." : editingSlide ? "Edit Slide" : "Create Slide"}
          </Button>
        </div>
      </div>

      <TagModal
        isOpen={tagModal !== null}
        onClose={() => setTagModal(null)}
        tag={tagModal?.tag}
        onSaved={handleTagSaved}
      />
    </div>
  );
}
